/**
 * The context an extension admin page gets (HUB-103): its own name, the hub
 * origin, and a `fetch` that targets `/ext/<name>/…` with the session cookie.
 * The route wraps the page in `ExtensionAdminProvider`; the page never has to
 * remember a wrapper. Known limit: no 401 auto-refresh — a stale session shows
 * a 401 until any typed client call refreshes it.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { hubBaseUrl } from '@/lib/hub-client';
import { buildExtensionRequest } from './extension-request';

export interface ExtensionAdmin {
  name: string;
  hubBaseUrl: string;
  fetch(path: string, init?: RequestInit): Promise<Response>;
}

export function createExtensionAdmin(
  name: string,
  baseUrl: string,
  fetchImpl: typeof fetch = (...args) => globalThis.fetch(...args),
): ExtensionAdmin {
  return {
    name,
    hubBaseUrl: baseUrl,
    fetch(path, init) {
      const req = buildExtensionRequest(baseUrl, name, path, init);
      return fetchImpl(req.url, req.init);
    },
  };
}

const ExtensionAdminContext = createContext<ExtensionAdmin | null>(null);

export function ExtensionAdminProvider({ name, children }: { name: string; children: ReactNode }) {
  const value = useMemo(() => createExtensionAdmin(name, hubBaseUrl), [name]);
  return <ExtensionAdminContext.Provider value={value}>{children}</ExtensionAdminContext.Provider>;
}

export function useExtensionAdmin(): ExtensionAdmin {
  const value = useContext(ExtensionAdminContext);
  if (!value) throw new Error('useExtensionAdmin() called outside an extension admin page');
  return value;
}
